import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { isApiConfigured } from "../auth-config";
import type { StudentActiveCohort } from "../auth/api-client";
import { getStudentMeCohorts } from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";

type StudentCohortContextValue = {
  cohorts: StudentActiveCohort[];
  hasCohorts: boolean;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
};

const StudentCohortContext = createContext<StudentCohortContextValue | null>(null);

export function StudentCohortProvider({ children }: { readonly children: ReactNode }) {
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const [cohorts, setCohorts] = useState<StudentActiveCohort[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!bearer || !isApiConfigured()) {
      setCohorts([]);
      setLoading(false);
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const data = await getStudentMeCohorts(bearer);
      setCohorts(data.cohorts);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load cohorts");
      setCohorts([]);
    } finally {
      setLoading(false);
    }
  }, [bearer]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const value = useMemo(
    () => ({
      cohorts,
      hasCohorts: cohorts.length > 0,
      loading,
      error,
      reload,
    }),
    [cohorts, loading, error, reload]
  );

  return <StudentCohortContext.Provider value={value}>{children}</StudentCohortContext.Provider>;
}

export function useStudentCohorts(): StudentCohortContextValue {
  const ctx = useContext(StudentCohortContext);
  if (!ctx) {
    throw new Error("useStudentCohorts must be used within StudentCohortProvider");
  }
  return ctx;
}

const EMPTY_COHORT_STATE: StudentCohortContextValue = {
  cohorts: [],
  hasCohorts: false,
  loading: false,
  error: null,
  reload: async () => {},
};

/** For pages shared with teachers that are not always inside StudentCohortProvider. */
export function useStudentCohortsOptional(): StudentCohortContextValue {
  return useContext(StudentCohortContext) ?? EMPTY_COHORT_STATE;
}
